/**
 * 管理员操作管理 Hook
 * 统一管理所有管理员相关的操作逻辑
 */
import { useState, useCallback } from "react";
import { AdminRecord } from "../config/panelTableConfig";
import { AdminService, CreateAdminParams, UpdateAdminParams } from "../services/adminService";
import { RoleService } from "../services/roleService";

export interface UseAdminOperationsReturn {
  // 操作状态
  submitting: boolean;
  roleSubmitting: boolean;

  // 管理员操作
  createAdmin: (params: CreateAdminParams) => Promise<boolean>;
  updateAdmin: (id: string, params: UpdateAdminParams) => Promise<boolean>;
  updateAdminStatus: (id: string, status: number) => Promise<boolean>;
  resetPassword: (id: string) => Promise<boolean>;
  deleteAdmin: (record: AdminRecord) => Promise<boolean>;
  batchDeleteAdmins: (ids: string[]) => Promise<boolean>;

  // 角色操作
  assignRolesToAdmin: (adminId: string, roleIds: string[]) => Promise<boolean>;
}

export const useAdminOperations = (): UseAdminOperationsReturn => {
  const [submitting, setSubmitting] = useState(false);
  const [roleSubmitting, setRoleSubmitting] = useState(false);

  // 创建管理员
  const createAdmin = useCallback(async (params: CreateAdminParams): Promise<boolean> => {
    setSubmitting(true);
    try {
      const response = await AdminService.createAdmin(params);
      return response.success;
    } catch (error) {
      console.error("创建管理员失败:", error);
      return false;
    } finally {
      setSubmitting(false);
    }
  }, []);

  // 更新管理员
  const updateAdmin = useCallback(async (id: string, params: UpdateAdminParams): Promise<boolean> => {
    setSubmitting(true);
    try {
      const response = await AdminService.updateAdmin(id, params);
      return response.success;
    } catch (error) {
      console.error("更新管理员失败:", error);
      return false;
    } finally {
      setSubmitting(false);
    }
  }, []);

  // 更新管理员状态
  const updateAdminStatus = useCallback(async (id: string, status: number): Promise<boolean> => {
    console.log("🔧 [updateAdminStatus] 开始更新状态:", { id, status });
    setSubmitting(true);
    try {
      const response = await AdminService.updateAdminStatus(id, status);
      console.log("🔧 [updateAdminStatus] API响应:", response);
      return response.success;
    } catch (error) {
      console.error("🔧 [updateAdminStatus] 更新管理员状态失败:", error);
      return false;
    } finally {
      setSubmitting(false);
    }
  }, []);

  // 重置管理员密码
  const resetPassword = useCallback(async (id: string): Promise<boolean> => {
    setSubmitting(true);
    try {
      const response = await AdminService.resetPassword(id);
      return response.success;
    } catch (error) {
      console.error("重置密码失败:", error);
      return false;
    } finally {
      setSubmitting(false);
    }
  }, []);

  // 删除管理员
  const deleteAdmin = useCallback(async (record: AdminRecord): Promise<boolean> => {
    setSubmitting(true);
    try {
      const response = await AdminService.deleteAdmin(record.id);
      return response.success;
    } catch (error) {
      console.error("删除管理员失败:", error);
      return false;
    } finally {
      setSubmitting(false);
    }
  }, []);

  // 批量删除管理员
  const batchDeleteAdmins = useCallback(async (ids: string[]): Promise<boolean> => {
    setSubmitting(true);
    try {
      const response = await AdminService.batchDeleteAdmins(ids);
      return response.success;
    } catch (error) {
      console.error("批量删除管理员失败:", error);
      return false;
    } finally {
      setSubmitting(false);
    }
  }, []);

  // 为管理员分配角色
  const assignRolesToAdmin = useCallback(async (adminId: string, roleIds: string[]): Promise<boolean> => {
    setRoleSubmitting(true);
    try {
      const response = await RoleService.assignRolesToAdmin({ adminId, roleIds });
      return response.success;
    } catch (error) {
      console.error("分配角色失败:", error);
      return false;
    } finally {
      setRoleSubmitting(false);
    }
  }, []);

  return {
    // 操作状态
    submitting,
    roleSubmitting,

    // 管理员操作
    createAdmin,
    updateAdmin,
    updateAdminStatus,
    resetPassword,
    deleteAdmin,
    batchDeleteAdmins,

    // 角色操作
    assignRolesToAdmin,
  };
};
